import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowLeft, Check, Loader2, AlertCircle, Sparkles,
  Play, Zap, User, Mail, Link2, CheckCircle, X, Pencil, GraduationCap, MapPin, Ruler
} from 'lucide-react';
import { API, POSITION_OPTIONS } from '../lib/constants';

const PositionSelector = ({ selected = [], onChange }) => {
  const categories = [
    { key: 'offense', label: 'Offense', positions: POSITION_OPTIONS.offense },
    { key: 'defense', label: 'Defense', positions: POSITION_OPTIONS.defense },
    { key: 'specialTeams', label: 'Special Teams', positions: POSITION_OPTIONS.specialTeams },
  ];
  const toggle = (pos) => onChange(selected.includes(pos) ? selected.filter(p => p !== pos) : [...selected, pos]);
  return (
    <div className="space-y-3">
      {categories.map(cat => (
        <div key={cat.key}>
          <p className="text-xs text-white/40 mb-1.5 uppercase tracking-wider">{cat.label}</p>
          <div className="flex flex-wrap gap-1.5">
            {cat.positions.map(pos => (
              <button key={pos} type="button" onClick={() => toggle(pos)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${selected.includes(pos) ? 'bg-[#0F5132] text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}>
                {pos}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const BUILD_MSGS = ['Reading your Hudl profile…', 'Pulling your photo & measurables…', 'Finding your highlights…', 'Building your page…'];

const CreateProfile = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 paste · 2 preview · 3 save
  const [hudlUrl, setHudlUrl] = useState('');
  const [building, setBuilding] = useState(false);
  const [buildMsg, setBuildMsg] = useState(0);
  const [buildError, setBuildError] = useState(false);
  const [manual, setManual] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const [username, setUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState(null);

  const [profileData, setProfileData] = useState({
    name: '', school: '', class_year: '', positions: [],
    height: '', weight: '', jersey_number: '', profile_image_url: '', gpa: '', x_url: '', location: '',
  });
  const [allVideoUrls, setAllVideoUrls] = useState([]);
  const [videoLoading, setVideoLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [coachSlug, setCoachSlug] = useState('');
  const [coachName, setCoachName] = useState('');

  // Prefill from homepage (?hudl= kicks off the instant build; ?username= carries the claimed name)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const u = params.get('username');
    if (u) setUsername(u.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 30));
    const c = params.get('coach');
    if (c) {
      setCoachSlug(c);
      axios.get(`${API}/api/coach/public/${c}`).then(res => setCoachName(res.data.coach?.display_name || '')).catch(() => {});
    }
    const h = params.get('hudl');
    if (h) { setHudlUrl(h); runBuild(h); }
    // eslint-disable-next-line
  }, []);

  // Cycle the building messages
  useEffect(() => {
    if (!building) return;
    const t = setInterval(() => setBuildMsg(m => (m + 1) % BUILD_MSGS.length), 1400);
    return () => clearInterval(t);
  }, [building]);

  // Username availability (needed at the save step)
  useEffect(() => {
    if (!username || username.length < 3) { setUsernameStatus(null); return; }
    setUsernameStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/api/check-username`, { username });
        setUsernameStatus(res.data.available ? 'available' : 'taken');
      } catch { setUsernameStatus(null); }
    }, 500);
    return () => clearTimeout(timer);
  }, [username]);

  const handleUsernameChange = (e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 30));

  const PLACEHOLDER_NAMES = ['test athlete', 'unknown player'];
  const PLACEHOLDER_SCHOOLS = ['test high school', 'unknown school'];
  const clean = (v, list) => (list.includes((v || '').toLowerCase().trim()) ? '' : v);

  // The magic moment: scrape the pasted Hudl link and render a real preview — no account needed
  const runBuild = async (urlArg) => {
    const url = (urlArg || hudlUrl || '').trim();
    if (!url) { navigate('/create-profile'); return; }
    setManual(false);
    setBuilding(true); setBuildError(false); setBuildMsg(0);
    const started = Date.now();
    let gotData = false;
    let hasName = false;
    try {
      const profileRes = await axios
        .post(`${API}/api/preview-hudl-basic`, { hudl_profile_url: url }, { timeout: 60000 })
        .catch(() => null);
      if (profileRes?.data?.success) {
        const data = profileRes.data.profile_data || {};
        const name = clean(data.name, PLACEHOLDER_NAMES);
        const school = clean(data.school, PLACEHOLDER_SCHOOLS);
        // The default Hudl avatar is not a real photo — don't count it as scraped data
        const img = /thumb-user\.svg/i.test(data.profile_image_url || '') ? '' : (data.profile_image_url || '');
        hasName = !!name;
        // Require a real name OR (school/photo) — placeholder payloads only carry a default ['QB'] + class year
        gotData = !!(name || school || img);
        if (gotData) {
          setProfileData(prev => ({
            ...prev,
            name: name || prev.name,
            school: school || prev.school,
            class_year: data.class_year || prev.class_year,
            positions: data.positions || prev.positions,
            height: data.height || prev.height,
            weight: data.weight || prev.weight,
            jersey_number: data.jersey_number || prev.jersey_number,
            profile_image_url: img || prev.profile_image_url,
            location: data.location || prev.location,
          }));
        }
      }
    } catch { /* fall through to manual */ }

    // Keep the build animation on screen at least ~1.6s so it feels intentional
    const elapsed = Date.now() - started;
    if (elapsed < 1600) await new Promise(r => setTimeout(r, 1600 - elapsed));

    setBuilding(false);
    setBuildError(!gotData);
    // Open the edit form whenever we couldn't read the link, or read it but still have no name
    setShowEdit(!gotData || !hasName);
    setStep(2);

    // Highlights in the background (non-blocking)
    setVideoLoading(true);
    axios.post(`${API}/api/list-hudl-videos`, { hudl_profile_url: url }, { timeout: 60000 })
      .then(r => { if (r.data?.success && r.data.video_urls) setAllVideoUrls(r.data.video_urls); })
      .catch(() => {})
      .finally(() => setVideoLoading(false));
  };

  const buildFromScratch = () => { setManual(true); setBuildError(true); setShowEdit(true); setStep(2); };

  const createProfile = async () => {
    if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    if (usernameStatus !== 'available') { toast.error('Pick an available username'); return; }
    if (!profileData.name) { toast.error('Add your name'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/confirm-hudl-import`, {
        username, email,
        profile_data: { ...profileData, hudl_url: hudlUrl },
        selected_video_data: [],
        remaining_video_urls: allVideoUrls,
        coach_slug: coachSlug || undefined,
      }, { withCredentials: true });

      if (res.data.success) {
        localStorage.setItem('userId', res.data.user_id);
        localStorage.setItem('username', res.data.username);
        localStorage.setItem('userEmail', email);
        toast.success(res.data.submitted_to_coach ? `Sent to ${res.data.submitted_to_coach}! Verify to go live.` : 'Your page is saved! Verify to go live.');
        navigate('/dashboard');
      } else if (res.data.claim_existing) {
        toast.success(res.data.message || 'We already built this page — claim it!');
        navigate(`/claim/${res.data.username}`);
      } else if (res.data.already_claimed) {
        toast.error(res.data.message || 'This profile already exists. Try signing in.');
        navigate('/signin');
      } else {
        toast.error(res.data.message || "We couldn't save your page. Please try again.");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (err.message || 'Something went wrong'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-8 bg-[#1a1a1a] rounded-full flex items-center justify-center"><span className="text-[#16A34A] font-black text-xs">O</span></div>
            <span className="text-lg font-black">Offersz</span>
          </div>
          <button onClick={() => navigate('/signin')} className="text-white/40 hover:text-white/70 text-sm" data-testid="create-signin-link">Sign in</button>
        </div>
      </div>

      {/* Building overlay */}
      {building && (
        <div className="fixed inset-0 z-[60] bg-black flex flex-col items-center justify-center px-6" data-testid="building-overlay">
          <div className="relative mb-8">
            <div className="w-24 h-24 rounded-full border-4 border-[#16A34A]/20 border-t-[#16A34A] animate-spin" />
            <Sparkles className="h-8 w-8 text-[#16A34A] absolute inset-0 m-auto animate-pulse" />
          </div>
          <p className="text-xl font-black mb-2" data-testid="building-message">{BUILD_MSGS[buildMsg]}</p>
          <p className="text-white/40 text-sm">Hang tight — this only takes a few seconds.</p>
        </div>
      )}

      <div className="pt-14 min-h-screen flex items-start justify-center px-4 pb-8">
        <div className="w-full max-w-lg mt-8 sm:mt-14">

          {coachName && (
            <div className="mb-6 bg-[#16A34A]/10 border border-[#16A34A]/25 rounded-2xl px-4 py-3 text-sm" data-testid="coach-submit-banner">
              <span className="text-white/60">Submitting your film to </span>
              <span className="font-bold text-[#4ade80]">{coachName}</span>
            </div>
          )}

          {/* STEP 1 — Paste your Hudl link */}
          {step === 1 && !building && (
            <div className="space-y-8 animate-in fade-in">
              <div>
                <h1 className="text-4xl sm:text-5xl font-black mb-3 leading-[1.05]">Your Next Offer<br /><span className="text-[#16A34A]">Starts Here.</span></h1>
                <p className="text-white/50 text-lg">Paste your Hudl link and watch your recruiting profile build itself — no sign-up needed to see it.</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-white/70 flex items-center gap-2"><Link2 className="h-4 w-4" /> Your Hudl profile link</label>
                <Input value={hudlUrl} onChange={(e) => setHudlUrl(e.target.value)} placeholder="https://www.hudl.com/profile/..."
                  onKeyPress={e => e.key === 'Enter' && hudlUrl && runBuild()} data-testid="create-hudl-input"
                  className="bg-white/5 border-white/10 text-white h-14 text-lg rounded-xl focus:border-[#16A34A] focus:ring-[#16A34A]/20" />
              </div>
              <Button onClick={() => runBuild()} disabled={!hudlUrl.trim()} data-testid="create-build-btn"
                className="w-full bg-[#16A34A] hover:bg-[#0F5132] text-white font-bold h-14 rounded-xl text-lg disabled:opacity-50">
                <Zap className="mr-2 h-5 w-5" /> Get started
              </Button>
              <button onClick={buildFromScratch} data-testid="create-manual-btn" className="w-full text-white/40 hover:text-white/70 text-sm py-2 transition-colors">
                No Hudl link? Build from scratch
              </button>
            </div>
          )}

          {/* STEP 2 — Instant preview */}
          {step === 2 && (
            <div className="space-y-6 animate-in fade-in">
              <div>
                {manual ? (
                  <>
                    <h1 className="text-3xl sm:text-4xl font-black mb-2">Add your basics</h1>
                    <p className="text-white/50">Fill in the essentials below and we'll build your page.</p>
                  </>
                ) : buildError ? (
                  <>
                    <h1 className="text-3xl sm:text-4xl font-black mb-2">Add your basics</h1>
                    <p className="text-white/50 flex items-center gap-1.5"><AlertCircle className="h-4 w-4 text-amber-400" /> We couldn't read that Hudl link — fill in the essentials and you're good to go.</p>
                  </>
                ) : (
                  <>
                    <h1 className="text-3xl sm:text-4xl font-black mb-2">This is your page 🔥</h1>
                    <p className="text-white/50">Built straight from your film. Save it to make it live.</p>
                  </>
                )}
              </div>

              {/* Preview card — looks like a finished page */}
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden" data-testid="instant-preview">
                <div className="bg-gradient-to-br from-[#16A34A]/20 to-[#16A34A]/5 p-6 flex items-center gap-4">
                  <div className="w-24 h-24 rounded-2xl overflow-hidden bg-white/10 flex-shrink-0 ring-2 ring-[#16A34A]/30">
                    {profileData.profile_image_url
                      ? <img src={profileData.profile_image_url} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-3xl font-black text-white/40">{profileData.name?.charAt(0) || username?.charAt(0) || '?'}</div>}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-2xl font-black truncate">{profileData.name || 'Your Name'}</h3>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {(profileData.positions || []).slice(0, 4).map(p => (
                        <span key={p} className="bg-[#16A34A] text-white text-xs font-bold px-2 py-0.5 rounded-full">{p}</span>
                      ))}
                      {profileData.jersey_number && <span className="bg-white/10 text-white/70 text-xs font-bold px-2 py-0.5 rounded-full">#{profileData.jersey_number}</span>}
                    </div>
                  </div>
                </div>
                <div className="p-5 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div className="flex items-center gap-2 text-white/70"><GraduationCap className="h-4 w-4 text-[#16A34A] flex-shrink-0" /><span className="truncate">{profileData.school || '—'}{profileData.class_year ? ` · '${String(profileData.class_year).slice(-2)}` : ''}</span></div>
                  <div className="flex items-center gap-2 text-white/70"><Ruler className="h-4 w-4 text-[#16A34A] flex-shrink-0" /><span className="truncate">{profileData.height || '—'}{profileData.weight ? ` · ${profileData.weight} lbs` : ''}</span></div>
                  {profileData.location && <div className="flex items-center gap-2 text-white/70"><MapPin className="h-4 w-4 text-[#16A34A] flex-shrink-0" /><span className="truncate">{profileData.location}</span></div>}
                  <div className="flex items-center gap-2 text-white/70"><Play className="h-4 w-4 text-[#16A34A] flex-shrink-0" />{videoLoading ? <span className="flex items-center gap-1 text-white/40"><Loader2 className="h-3 w-3 animate-spin" /> finding highlights…</span> : <span>{allVideoUrls.length} highlight{allVideoUrls.length !== 1 ? 's' : ''}</span>}</div>
                </div>
                <button onClick={() => setShowEdit(v => !v)} data-testid="toggle-edit-preview" className="w-full border-t border-white/10 py-2.5 text-xs font-semibold text-white/50 hover:text-white/80 flex items-center justify-center gap-1.5">
                  <Pencil className="h-3 w-3" /> {showEdit ? 'Hide details' : 'Edit details'}
                </button>

                {showEdit && (
                  <div className="p-5 border-t border-white/10 space-y-4" data-testid="preview-edit-fields">
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="text-xs text-white/40 mb-1 block">Full Name</label><Input value={profileData.name} onChange={e => setProfileData(p => ({ ...p, name: e.target.value }))} data-testid="edit-name" className="bg-white/5 border-white/10 text-white h-10 rounded-lg" /></div>
                      <div><label className="text-xs text-white/40 mb-1 block">School</label><Input value={profileData.school} onChange={e => setProfileData(p => ({ ...p, school: e.target.value }))} className="bg-white/5 border-white/10 text-white h-10 rounded-lg" /></div>
                      <div><label className="text-xs text-white/40 mb-1 block">Class Year</label><Input value={profileData.class_year} onChange={e => setProfileData(p => ({ ...p, class_year: e.target.value }))} placeholder="2027" className="bg-white/5 border-white/10 text-white h-10 rounded-lg" /></div>
                      <div><label className="text-xs text-white/40 mb-1 block">Jersey #</label><Input value={profileData.jersey_number} onChange={e => setProfileData(p => ({ ...p, jersey_number: e.target.value }))} className="bg-white/5 border-white/10 text-white h-10 rounded-lg" /></div>
                      <div><label className="text-xs text-white/40 mb-1 block">Height</label><Input value={profileData.height} onChange={e => setProfileData(p => ({ ...p, height: e.target.value }))} placeholder="6'2&quot;" className="bg-white/5 border-white/10 text-white h-10 rounded-lg" /></div>
                      <div><label className="text-xs text-white/40 mb-1 block">Weight (lbs)</label><Input value={profileData.weight} onChange={e => setProfileData(p => ({ ...p, weight: e.target.value }))} className="bg-white/5 border-white/10 text-white h-10 rounded-lg" /></div>
                    </div>
                    <div><label className="text-xs text-white/40 mb-2 block">Positions</label><PositionSelector selected={profileData.positions} onChange={positions => setProfileData(p => ({ ...p, positions }))} /></div>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="preview-back-btn" className="border-white/10 text-white hover:bg-white/5 rounded-xl"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
                <Button onClick={() => setStep(3)} disabled={!profileData.name} data-testid="save-page-btn" className="flex-1 bg-[#16A34A] hover:bg-[#0F5132] text-white font-bold h-12 rounded-xl text-lg disabled:opacity-50">
                  Save my page <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3 — Save (email + X + username) */}
          {step === 3 && (
            <div className="space-y-6 animate-in fade-in">
              <div>
                <h1 className="text-3xl sm:text-4xl font-black mb-2">Save your page</h1>
                <p className="text-white/50">No password, ever. We send a one-tap link and a 6-digit code.</p>
              </div>

              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
                  {profileData.profile_image_url ? <img src={profileData.profile_image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-bold text-white/40">{profileData.name?.charAt(0) || '?'}</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{profileData.name}</p>
                  <p className="text-white/40 text-xs truncate">{[profileData.school, profileData.positions?.join(', ')].filter(Boolean).join(' • ') || 'Football Player'}</p>
                </div>
                <Badge className="bg-[#16A34A]/10 text-[#16A34A] border-[#16A34A]/20">{allVideoUrls.length} video{allVideoUrls.length !== 1 ? 's' : ''}</Badge>
              </div>

              {/* Username */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-white/70 flex items-center gap-2"><User className="h-4 w-4" /> Your link</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm">offersz.co/</span>
                  <Input value={username} onChange={handleUsernameChange} placeholder="yourname" data-testid="onboarding-username"
                    className="bg-white/5 border-white/10 text-white pl-24 h-12 rounded-xl focus:border-[#16A34A]" />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {usernameStatus === 'checking' && <Loader2 className="h-4 w-4 animate-spin text-white/30" />}
                    {usernameStatus === 'available' && <CheckCircle className="h-5 w-5 text-green-400" />}
                    {usernameStatus === 'taken' && <X className="h-5 w-5 text-red-400" />}
                  </div>
                </div>
                {usernameStatus === 'taken' && <p className="text-red-400 text-xs flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Username taken</p>}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-white/70 flex items-center gap-2"><Mail className="h-4 w-4" /> Email</label>
                <Input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@email.com"
                  data-testid="create-email-input" className="bg-white/5 border-white/10 text-white h-12 rounded-xl" />
              </div>

              {/* X — optional */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-white/70 flex items-center gap-2"><X className="h-4 w-4" /> X (Twitter) <span className="text-white/50 text-xs font-medium ml-1">Optional</span></label>
                <Input value={profileData.x_url} onChange={e => setProfileData(p => ({ ...p, x_url: e.target.value }))} placeholder="https://x.com/yourhandle"
                  data-testid="onboarding-x-url" className="bg-white/5 border-white/10 text-white h-12 rounded-xl" />
                <p className="text-white/30 text-xs">Players land on your Offersz link straight from your X bio — add it now or later from your dashboard.</p>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(2)} data-testid="save-back-btn" className="border-white/10 text-white hover:bg-white/5 rounded-xl"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
                <Button onClick={createProfile} disabled={!email || usernameStatus !== 'available' || creating}
                  data-testid="create-profile-submit-btn" className="flex-1 bg-[#16A34A] hover:bg-[#0F5132] text-white font-bold h-12 rounded-xl text-lg disabled:opacity-50">
                  {creating ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Saving…</> : <><Zap className="mr-2 h-5 w-5" /> Save & go live</>}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateProfile;
